import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Radio, Loader2 } from 'lucide-react';
import { api } from '../api';
import { useCommunity } from '../hooks/useCommunity';
import RoomCard from '../components/RoomCard';
import EmptyState from '../components/EmptyState';

const CommunitySessions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { community, loading: communityLoading } = useCommunity(id);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const sessionsData = await api.get(`/rooms?community_id=${id}`);
        setSessions(Array.isArray(sessionsData) ? sessionsData : []);
      } catch (err) {
        console.error('Failed to fetch sessions', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const liveSessions = sessions.filter(s => s.status?.toLowerCase() === 'live');
  const otherSessions = sessions.filter(s => s.status?.toLowerCase() !== 'live');

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-gray-400">
      <Loader2 size={40} className="animate-spin mb-4 text-brand-indigo" />
      <p className="font-bold uppercase tracking-widest text-[10px]">Loading Sessions...</p>
    </div>
  );

  return (
    <div className="min-w-0 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-12">
            <div className="text-center md:text-left">
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">Live Sessions</h1>
              <p className="text-gray-500 font-medium max-w-xl">
                {community ? `Interactive events and archives for the ${community.name} hub.` : 'Interactive events and knowledge archives.'}
              </p>
            </div>
            <button 
               onClick={() => navigate(`/rm/lv/new?community_id=${id}`)}
               className="w-full md:w-auto flex items-center justify-center gap-2 bg-brand-indigo text-white px-8 py-4 rounded-2xl font-bold shadow-xl shadow-brand-indigo/10 hover:bg-brand-indigo/90 transition-all active:scale-[0.98] text-xs uppercase tracking-widest"
            >
              <Plus size={20} />
              Schedule Session
            </button>
          </div>

          {liveSessions.length > 0 && (
            <section className="mb-16">
              <div className="flex items-center gap-2 mb-6">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em]">Happening Now</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {liveSessions.map(session => (
                  <RoomCard key={session.id} room={session} featured />
                ))}
              </div>
            </section>
          )}

          <section>
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em]">
                {liveSessions.length > 0 ? 'Upcoming & Past' : 'All Sessions'}
              </h2>
            </div>
            
            {otherSessions.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {otherSessions.map(session => (
                  <RoomCard key={session.id} room={session} />
                ))}
              </div>
            ) : liveSessions.length === 0 ? (
              <EmptyState 
                icon={Radio}
                title="Quiet for now"
                description="No sessions found in this community hub yet."
                action={
                  <button 
                    onClick={() => navigate(`/rm/lv/new?community_id=${id}`)}
                    className="flex items-center gap-2 bg-brand-indigo text-white px-8 py-3 rounded-xl font-bold text-sm shadow-lg shadow-brand-indigo/20 hover:bg-brand-indigo/90 transition-all"
                  >
                    <Plus size={18} />
                    Host the first session
                  </button>
                }
              />
            ) : null}
          </section>
        </div>
    </div>
  );
};

export default CommunitySessions;
